import {
  createItem,
  readItems,
  readItem,
  updateItem,
  deleteItem,
} from '@directus/sdk';
import { getDirectusClient } from '../lib/directus';

const directus = getDirectusClient();

// Crear un nuevo formulario
export const crearFormulario = async formData => {
  try {
    const response = await directus.request(
      createItem('formularios', {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        bodega: formData.bodega,
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Crear campos para un formulario
export const crearCamposFormulario = async (formularioId, campos) => {
  try {
    const response = await directus.request(
      createItem('campos_formulario', campos)
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Obtener todos los formularios con sus campos
export const obtenerFormularios = async (userRoleID, userBodega) => {
  try {
    const formularios = await directus.request(
      readItems('formularios', {
        fields: ['*', 'campos_formulario.*', 'bodega.nombre'],
        filter: {
          ...(userRoleID !== 'adminapp' && userRoleID !== 'Administrator'
            ? {
               
              
                  bodega: {
                    _eq: userBodega,
                  },
              
              }
            : {}),
        },
        limit: -1,
        sort: ['-id'],
      })
    );
    return formularios;
  } catch (error) {
    throw error;
  }
};

// Obtener un formulario específico por ID
export const obtenerFormulario = async id => {
  try {
    const formulario = await directus.request(
      readItem('formularios', id, {
        fields: ['*', 'campos_formulario.*'],
      })
    );
    return formulario;
  } catch (error) {
    throw error;
  }
};

// Actualizar el orden de un campo específico
export const actualizarOrdenCampo = async (campoId, nuevoOrden) => {
  try {
    const response = await directus.request(
      updateItem('campos_formulario', campoId, {
        orden: nuevoOrden,
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Obtener el siguiente orden disponible para un campo
export const obtenerSiguienteOrden = async formularioId => {
  try {
    const formulario = await obtenerFormulario(formularioId);
    if (
      !formulario.campos_formulario ||
      formulario.campos_formulario.length === 0
    ) {
      return 1;
    }
    const maxOrden = Math.max(
      ...formulario.campos_formulario.map(campo => campo.orden || 0)
    );
    return maxOrden + 1;
  } catch (error) {
    throw error;
  }
};

// Eliminar un formulario y sus campos
export const eliminarFormulario = async id => {
  try {
    // Primero eliminamos los campos asociados al formulario
    const formulario = await obtenerFormulario(id);
    if (formulario.campos_formulario) {
      const deletePromises = formulario.campos_formulario.map(campo =>
        directus.request(deleteItem('campos_formulario', campo.id))
      );
      await Promise.all(deletePromises);
    }

    // Luego eliminamos el formulario
    await directus.request(deleteItem('formularios', id));
    return true;
  } catch (error) {
    throw error;
  }
};

// Eliminar un campo específico de un formulario
export const eliminarCampoFormulario = async campoId => {
  try {
    await directus.request(deleteItem('campos_formulario', campoId));
    return true;
  } catch (error) {
    throw error;
  }
};

// Actualizar un campo específico de un formulario
export const actualizarCampoFormulario = async (campoId, datosCampo) => {
  try {
    const response = await directus.request(
      updateItem('campos_formulario', campoId, datosCampo)
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Crear un nuevo campo en un formulario existente
export const crearCampoFormulario = async (formularioId, campoData) => {
  try {
    const response = await directus.request(
      createItem('campos_formulario', {
        formulario: formularioId,
        ...campoData,
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const obtenerRespuestas = async formularioId => {
  try {
    const response = await directus.request(
      readItems('respuestas_formulario', {
        fields: ['*'],
        sort: ['-id'],
        limit: -1,
        filter: {
          formulario: {
            _eq: formularioId,
          },
        },
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const obtenerDetallesRespuesta = async respuestaId => {
  try {
    const response = await directus.request(
      readItems('respuestas_campos', {
        fields: ['*', 'campo.*'],
        limit: -1,
        filter: {
          respuestas_formulario: {
            _eq: respuestaId,
          },
        },
        deep: {
          campo: {
            _filter: {
              id: {
                _nnull: true,
              },
            },
          },
        },
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Nueva función optimizada para obtener todos los detalles de respuestas en una sola petición
export const obtenerTodosDetallesRespuestas = async (formularioId) => {
  try {
    const response = await directus.request(
      readItems('respuestas_campos', {
        fields: ['*', 'campo.*', 'respuestas_formulario.id'],
        limit: -1,
        filter: {
          respuestas_formulario: {
            formulario: {
              _eq: formularioId,
            },
          },
        },
        deep: {
          campo: {
            _filter: {
              id: {
                _nnull: true,
              },
            },
          },
        },
        sort: ['-id'],
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// Función super optimizada que obtiene formulario, respuestas y detalles en una sola petición
export const obtenerFormularioCompleto = async (formularioId) => {
  try {
    const response = await directus.request(
      readItems('formularios', {
        fields: [
          '*',
          'campos_formulario.*',
          'respuestas_formulario.*',
          'respuestas_formulario.respuestas_campos.*',
          'respuestas_formulario.respuestas_campos.campo.*'
        ],
        filter: {
          id: {
            _eq: formularioId,
          },
        },
        limit: 1,
        sort: ['-id'],
        deep: {
          respuestas_formulario: {
            _sort: ['-id'],
            _limit: -1
          }
        }
      })
    );
    return response[0];
  } catch (error) {
    throw error;
  }
};

export const buscarRespuestasPorValor = async (formularioId, valorBusqueda) => {
  try {
    const response = await directus.request(
      readItems('respuestas_campos', {
        fields: ['*', 'campo.*', 'respuestas_formulario.*'],
        limit: -1,
        filter: {
          _and: [
            {
              campo: {
                formulario: {
                  id: {
                    _eq: formularioId,
                  },
                },
              },
            },
            {
              valor: {
                _contains: valorBusqueda,
              },
            },
          ],
        },
      })
    );
    return response;
  } catch (error) {
    throw error;
  }
};
